import React, { useCallback, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import useFetch from '../../hooks/useFetch';
import MainLayout from '../../layouts/MainLayout';
import './styles/MCQPractice.css';
import ChatGPTEVALANSComponent from '../../components/ChatGPTEVALANSComponent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMicrochip, faMicrophone } from '@fortawesome/free-solid-svg-icons';

const TestBookSATTest = () => {
    const authState = useSelector((state) => state.authReducer);
  const [fetchData, { loading }] = useFetch();
  const navigate = useNavigate();
  const location = useLocation();

  // State hooks
  const [setData, setSetData] = useState({
    setId: location.state.setId,
    setTitle: location.state.setTitle,
    seriesId: location.state.seriesId,
    seriesTitle: location.state.seriesTitle,
    examId: location.state.examId,
    examTitle: location.state.examTitle,
    examType: location.state.examType,
    timeLimitInMin: location.state.timeLimitInMin,
    isRepeatPractice: location.state.isRepeatPractice,
  });
  const [practiceTitle, setPracticeTitle] = useState('TestBook - Descriptive Practice');
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [timer, setTimer] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [answerBeforeSubmitMsg, setAnswerBeforeSubmitMsg] = useState(null);

  const [isCheckedAISupport, setIsCheckedAISupport] = useState(false);
  const [repeatPracticeId, setRepeatPracticeId] = useState(null);
  const [teacherCode, setTeacherCode] = useState('');
  const [teacherName, setTeacherName] = useState('');

  // Fetch questions and handle timer
  useEffect(() => {
    if (authState.isLoggedIn) fetchQuestions();
    setStartTime(new Date());
    const intervalId = setInterval(() => {
      setTimer((prevTimer) => prevTimer + 1);
    }, 1000);
    return () => clearInterval(intervalId);
  }, [authState.isLoggedIn, setData.setId]);

  const fetchQuestions = useCallback(() => {
    if (!setData.setId || !authState.token) return;

    const config = {
      url: '/ttbquestionmaster/sat',
      method: 'post',
      headers: { Authorization: authState.token },
      data: { setId: setData.setId },
    };

    fetchData(config, { showSuccessToast: false })
      .then((response) => {
        if (response && Array.isArray(response.questionlist)) {
          setQuestions(response.questionlist);
          const initialAnswers = response.questionlist.map((question) => ({
            userAnswer: '',
            aiEvaluation: '',
            selfEvaluation: '',
            aiMarks: 0,
            selfMarks: 0,
            questionId: question._id,
            questionTitle: question.question,
            teacherEvaluation: '',
            teacherMarks: 0,
          }));
          setAnswers(initialAnswers);
        } else {
          console.error('Invalid response format: Assignments not found in response');
        }
      })
      .catch((error) => {
        console.error('Error fetching Assignments:', error);
      });
  }, [fetchData, setData.setId, authState.token]);

  const handleSavePractice = () => {
    if (submitted) return;

    const isFilled = answers.every((answer) => answer.userAnswer.trim() !== '');
    if (!isFilled) {
      setAnswerBeforeSubmitMsg('Complete your exam. Give the answers before submitting it.');
      return;
    }

    setEndTime(new Date());
    setSubmitted(true);
    savePracticeToDB();
  };

  const savePracticeToDB = async () => {
    const userId = authState.user._id;
    const numberOfQuestions = questions.length;
    const totalMarksObtained = answers.reduce((acc, answer) => acc + answer.aiMarks + answer.selfMarks, 0);
    const fullMarks = numberOfQuestions * 10;
    const incorrectAnswers = answers.filter((answer) => answer.userAnswer.trim() === '').length;

    const requestData = {
      setQuestionType: 'Descriptive',
      userId,
      setId: setData.setId,
      setTitle: setData.setTitle,
      seriesId: setData.seriesId,
      seriesTitle: setData.seriesTitle,
      examId: setData.examId,
      examTitle: setData.examTitle,
      examType: setData.examType,
      practiceTitle,
      isRepeatPractice: setData.isRepeatPractice,
      repeatPracticeId,
      numberOfQuestions,
      incorrectAnswers,
      datePracticed: startTime,
      practiceTimeStarted: startTime,
      practiceTimeEnded: endTime,
      marksObtained: totalMarksObtained,
      percentage: (totalMarksObtained / fullMarks) * 100,
      fullMarks,
      answers,
      remarks: '',
      tags: '',
      practiceLocation: '',
      mentorName: '',
      mentorRemarks: '',
      selfFeedback: '',
      locationIP: '',
      hasExplanation: 0,
      isActive: 1,
      isDeleted: 0,
      createBy: userId,
      timeTaken: timer,
      timeLimit: setData.timeLimitInMin,
      satPracticeCode: generatePracticeCode(),
    };

    try {
      const config = {
        url: '/ttbsavepractice/ttbsatsave',
        method: 'post',
        data: requestData,
        headers: { Authorization: authState.token },
      };
      await fetchData(config);
      navigate('/testbook-historysat');
    } catch (error) {
      console.error('An error occurred while fetching data:', error);
    }
  };

  const generatePracticeCode = () => {
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const yyyy = now.getFullYear();
    const hh = String(now.getHours()).padStart(2, '0');
    const min = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');
    return `${dd}${mm}${yyyy}${hh}${min}${ss}`;
  };

  const formatTime = (timeInSeconds) => {
    const hours = Math.floor(timeInSeconds / 3600);
    const minutes = Math.floor((timeInSeconds % 3600) / 60);
    const seconds = timeInSeconds % 60;
    return `${hours}Hr ${minutes}Min ${seconds}Sec`;
  };

  const handleChange = (e, index, field) => {
    const { value } = e.target;
    setAnswers((prev) => {
      const updatedAnswers = [...prev];
      updatedAnswers[index] = { ...updatedAnswers[index], [field]: value }; // Dynamically set field
      return updatedAnswers;
    });
  };

    return (
        <MainLayout>
            <div className="py-4 mx-4">
                <h1 className="practice-test-heading text-yellow-300 mb-4" style={{ fontSize: '1.5rem' }}>
                    Think5Whys Short Answer Practice
                </h1>
                <div className="border-2 border-cyan-400 py-4 px-4">
                    <p className="text-white">Welcome, {authState.user?.name || 'Guest'}</p>
                    <p className="text-white">Practice Set: {setData.setTitle}</p>
                    <p className="text-white">Practice Series: {setData.seriesTitle}</p>
                    <p className="text-white">Practice for Exam: {setData.examTitle} - {setData.examType}</p>
                    <p className="text-white">Your Test Begins at: {startTime && startTime.toLocaleString()}</p>
                    <p className="text-yellow-300">Time Limit: {setData.timeLimitInMin} Minutes</p>
                    <p className="text-white">Time Taken: {formatTime(timer)}</p>
                </div>
                {questions.map((question, index) => (
                    <div key={index} className="py-4 px-2 mt-2 text-white">
                         <div className="flex flex-col py-4 px-4 mb-2 cursor-pointer border-2 border-purple-600">   
                            <p>Question:</p>
                            <p className="text-yellow-300">{question.question}</p>
        
                            {/* User's Answer */}
                            <p>You Answer:</p>
                            <textarea
                                value={answers[index]?.userAnswer || ''}
                                placeholder="Write answer here..."
                                onChange={(e) => handleChange(e, index, 'userAnswer')}
                                maxLength="2500"
                                style={{
                                    border: '1px solid #ccc',
                                    padding: '2px',
                                    backgroundColor: '#f0f0f0',
                                    color: '#000000',
                                    maxWidth: '100%',
                                    maxHeight: '150px',
                                }}
                                className="py-2 px-2"
                            />
                        </div>
                    
                        {/* AI Support Section */}
                        <div className="flex flex-col py-4 px-4 cursor-pointer border-2 border-yellow-200">
                            <div className="w-full">
                                <input
                                    type="checkbox"
                                    className="mr-2 cursor-pointer"
                                    checked={isCheckedAISupport}
                                    onChange={() => setIsCheckedAISupport(!isCheckedAISupport)}
                                />
                                <span className="text-lg font-medium text-white">Verify with AI</span>
                                <FontAwesomeIcon icon={faMicrochip} className="text-yellow-500 text-xl ml-3" />
                            </div>
    
                            {isCheckedAISupport && answers[index]?.userAnswer && (
                                <div className="mb-1">
                                    <ChatGPTEVALANSComponent
                                        param1={`Question: ${question.question}`}
                                        param2={` -- My Answer: ${answers[index]?.userAnswer} -- Evaluate my answer and give me marks out of 10.`}
                                    />
                                </div>
                            )}
    
                            {/* AI Evaluation Section */}
                            <p className="question text-yellow-300">AI Evaluation</p>
                            <textarea
                                value={answers[index]?.aiEvaluation || ''}
                                placeholder="Paste AI answer here..."
                                onChange={(e) => handleChange(e, index, 'aiEvaluation')}
                                maxLength="2500"
                                style={{
                                    border: '1px solid #ccc',
                                    padding: '2px',
                                    backgroundColor: '#f0f0f0',
                                    color: '#000000',
                                    maxWidth: '100%',
                                    maxHeight: '150px',
                                }}
                            />
    
                            {/* AI Evaluated Marks */}
                            <p className="question text-yellow-300">AI Evaluated Marks</p>
                            <input
                                type="number"
                                value={answers[index]?.aiMarks || ''}
                                placeholder="Write here.."
                                onChange={(e) => handleChange(e, index, 'aiMarks')}
                                style={{
                                    border: '1px solid #ccc',
                                    padding: '2px',
                                    backgroundColor: '#f0f0f0',
                                    color: '#000000',
                                    maxWidth: '100px',
                                }}
                                className="h-10"
                            />
                        </div>
    
                        {/* Self Evaluation Section */}
                        <div className="px-4 py-4 border-2 border-blue-400 mt-2 mb-2 text-white">
                            <p className="question">Self Evaluation Remarks:</p>
                            <textarea
                                value={answers[index]?.selfEvaluation || ''}
                                placeholder="Write your own evaluation here..."
                                onChange={(e) => handleChange(e, index, 'selfEvaluation')}
                                maxLength="2500"
                                style={{
                                    border: '1px solid #ccc',
                                    padding: '2px',
                                    backgroundColor: '#f0f0f0',
                                    color: '#000000',
                                    maxWidth: '100%',
                                    maxHeight: '150px',
                                }}
                            />
    
                            <p className="question">Self Evaluation Marks:</p>
                            <input
                                type="number"
                                value={answers[index]?.selfMarks || ''}
                                onChange={(e) => handleChange(e, index, 'selfMarks')}
                                style={{
                                    border: '1px solid #ccc',
                                    padding: '2px',
                                    backgroundColor: '#f0f0f0',
                                    color: '#000000',
                                    maxWidth: '100px',
                                }}
                                className="h-10"
                            />
                        </div>
    
                        {/* Teacher Code Section */}
                        <div className="px-4 py-4 border-2 border-blue-400 mt-2 mb-2 text-white">
                            <p className="question">Teacher Code:</p>
                            <input
                                type="text"
                                value={teacherCode}
                                placeholder="14 Character Teacher Code"
                                onChange={(e) => setTeacherCode(e.target.value)}
                                style={{
                                    border: '1px solid #ccc',
                                    padding: '2px',
                                    backgroundColor: '#f0f0f0',
                                    color: '#000000',
                                    width: '150px',
                                }}
                            />
    
                            <p className="question">Teacher Name (Optional):</p>
                            <input
                                type="text"
                                value={teacherName}
                                placeholder="Teacher Name..."
                                onChange={(e) => setTeacherName(e.target.value)}
                                style={{
                                    border: '1px solid #ccc',
                                    padding: '2px',
                                    backgroundColor: '#f0f0f0',
                                    color: '#000000',
                                    width: '300px',
                                }}
                            />
                        </div>
                    </div>
                ))}
    
                {!submitted && (
                    <div className="text-red-600">{answerBeforeSubmitMsg}</div>
                )}
    
                {/* Submit Button */}
                <button
                    className="save-practice-button"
                    onClick={handleSavePractice}
                    disabled={submitted}
                >
                    Submit for Teacher's Evaluation
                </button>
    
                {/* Submission Details */}
                {submitted && (
                    <div className="submission-details text-white">
                        <p>{authState.user?.name || 'Guest'} Your Practice Test Is Over.</p>
                        <p>Start Time: {startTime && startTime.toLocaleString()}</p>
                        <p>End Time: {endTime && endTime.toLocaleString()}</p>
                        <p>Time Taken: {formatTime(timer)}</p>
                    </div>
                )}
            </div>
        </MainLayout>
    );     
};

export default TestBookSATTest;
